interface TrackViewItemInput {
  product: {
    handle: string
    name?: string
    category?: { name?: string }
    quantity?: number
    reference?: string
    supplier?: { name?: string }
    manufacturer?: { name?: string }
    [key: string]: unknown
  }
  price?: number
  selectedFields?: Record<string, string>
  supplierName?: string
  index?: number
}

/**
 * Composable for tracking product view events with Google Analytics 4
 * Prevents duplicate tracking of the same product in a single session
 */
export const useProductTracking = () => {
  const gtm = useGtm()
  const helper = useHelper()
  const log = useLogger('useProductTracking')
  const { $i18n } = useNuxtApp()
  const pageSection = usePageSection()
  // While a ProductAlternativesBottomSheet is open, view_item events fired
  // from within it (e.g. favouriting a product) should report the overlay's
  // section instead of the underlying route's.
  const { section: alternativesSection } = useAlternativesTrackingSection()

  // Track last viewed products to prevent duplicate tracking
  const lastTrackedHandles = ref<Set<string>>(new Set())

  /**
   * Track a product view event for Google Analytics 4
   * @param product - The product object to track (must have handle property)
   * @param price - Precomputed price in euros (campaign-aware)
   * @param selectedFields - Selected variant fields (excluding reference)
   * @param supplierName - Optional supplier name (will be extracted from product if not provided)
   * @param index - Optional index for GA4 tracking (defaults to 0)
   */
  const trackViewItem = ({
    product,
    price,
    selectedFields,
    supplierName,
    index = 1,
  }: TrackViewItemInput) => {
    if (!product) {
      log.warn('useProductTracking: Product is required for tracking')
      return
    }

    // Only track if this product hasn't been tracked in this session/view
    if (lastTrackedHandles.value.has(product.handle)) {
      return
    }

    lastTrackedHandles.value.add(product.handle)

    const supplier = supplierName
      || product.supplier?.name
      || product.manufacturer?.name
      || 'Unknown Supplier'

    // Clear ecommerce object and track view_item event
    gtm?.trackEvent({ ecommerce: null })
    gtm?.trackEvent({
      event: 'view_item',
      page_section: alternativesSection.value || pageSection,
      ecommerce: {
        currency: 'EUR',
        items: [
          helper.convertProductToGa4EcommerceItem({
            $i18n,
            index,
            data: product,
            supplierName: supplier,
            price,
            selectedFields,
          }),
        ],
      },
    })
  }

  /**
   * Clear all tracked product handles (useful for resetting tracking state)
   */
  const clearTrackedHandles = () => {
    lastTrackedHandles.value.clear()
  }

  /**
   * Check if a product has been tracked in the current session
   * @param productHandle - The product handle to check
   */
  const hasBeenTracked = (productHandle: string) => {
    return lastTrackedHandles.value.has(productHandle)
  }

  function getTrackingPriceEuros(selectedProduct: any, currentPriceCents: number): number | undefined {
    const p = selectedProduct
    const cents = p.campaign?.status === 'ACTIVE' && typeof p.campaign.afterPrice === 'number'
      ? p.campaign.afterPrice
      : currentPriceCents
    return cents != null ? cents / 100 : undefined
  }

  return {
    trackViewItem,
    clearTrackedHandles,
    hasBeenTracked,
    getTrackingPriceEuros,
  }
}
